import { useState, useEffect } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import Card from '../components/Card';
import Button from '../components/Button';
import Hero from '../components/Hero';
import FeaturedSlider from '../components/FeaturedSlider';
import Stats from '../components/Stats';
import { getAllProducts, Product as ApiProduct } from '../services/productService';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Define the card type
interface CardType {
  id: number;
  title: string;
  creator: string;
  price: number;
  isLiked: boolean;
  badges?: string[];
  isNew?: boolean;
  discount?: number;
  imageUrl: string;
}

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

// Card wrapper component to handle hover animations without layout shifts
const CardWrapper = ({ children, variants }: {
  children: React.ReactNode,
  variants: Variants
}) => {
  return (
    <div className="relative" style={{
      height: '100%',
      overflow: 'visible',
      isolation: 'isolate', // Create a new stacking context
      transform: 'translateZ(0)', // Force GPU acceleration
      contain: 'layout', // Contain layout changes
      margin: '0.5rem 0' // Add padding to account for scale effect
    }}>
      <motion.div
        variants={variants}
        className="relative h-full"
        style={{
          transformOrigin: 'center center',
          willChange: 'transform',
          transform: 'translateZ(0)',
          position: 'relative',
          zIndex: 1
        }}
        whileHover={{
          scale: 1.03, // Use scale instead of y position change
          zIndex: 20,
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
          transition: {
            duration: 0.2,
            ease: "easeOut"
          }
        }}
      >
        {children}
      </motion.div>
    </div>
  );
};

const HomePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [displayedStaffPicks, setDisplayedStaffPicks] = useState(4);
  const [email, setEmail] = useState('');
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [staffPickCards, setStaffPickCards] = useState<CardType[]>([]);
  const [popularCards, setPopularCards] = useState<CardType[]>([]);
  const [recentCards, setRecentCards] = useState<CardType[]>([]);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const products = await getAllProducts();

        // Create placeholder products if API returns empty array
        const placeholderProducts = products && products.length > 0 ? products : [
          {
            id: 1,
            title: "Premium Fantasy Dungeon Pack",
            description: "Complete 3D dungeon package with animated props and lighting effects",
            price: 149.99,
            images: ["https://images.unsplash.com/photo-1560419015-7c427e8ae5ba?q=80&w=1470&auto=format&fit=crop"],
            badges: ["3D Models", "Environments", "Best Seller"],
            category: "3D Assets",
            tags: ["3D Models", "Environments", "Fantasy", "Dungeon"],
            sellerId: "seller123",
            sellerName: "PixelWizard",
            rating: 4.8,
            releaseDate: "2023-05-12",
            likes: 2450,
            downloads: 1800,
            features: ["High quality textures", "PBR materials", "Optimized meshes", "Animated props"]
          },
          {
            id: 2,
            title: "Sci-Fi UI Interface Kit",
            description: "Futuristic UI elements and HUD components for space-themed games",
            price: 79.99,
            images: ["https://images.unsplash.com/photo-1548391350-968f58dedaed?q=80&w=1587&auto=format&fit=crop"],
            badges: ["UI Kits", "2D Assets", "New"],
            category: "UI Kits",
            tags: ["UI", "Interface", "Sci-Fi", "HUD"],
            sellerId: "seller456",
            sellerName: "UIExpert",
            rating: 4.6,
            releaseDate: "2023-06-20",
            likes: 1830,
            downloads: 1200,
            features: ["Vector shapes", "Layered PSD files", "Customizable elements", "Multiple color schemes"]
          },
          {
            id: 3,
            title: "Advanced Character Controller",
            description: "Highly optimized character movement system with advanced physics",
            price: 59.99,
            images: ["https://images.unsplash.com/photo-1550745165-9bc0b252726f?q=80&w=1470&auto=format&fit=crop"],
            badges: ["Scripts", "Physics", "Editor's Choice"],
            category: "Scripts",
            tags: ["Character Controller", "Physics", "Movement", "Animation"],
            sellerId: "seller789",
            sellerName: "CodeMaster",
            rating: 4.9,
            releaseDate: "2023-04-05",
            likes: 2180,
            downloads: 1650,
            features: ["Advanced physics", "Animation integration", "Input handling", "Cross-platform support"]
          },
          {
            id: 4,
            title: "RPG Inventory System",
            description: "Complete inventory management system with crafting and storage features",
            price: 39.99,
            images: ["https://images.unsplash.com/photo-1614064641938-3bbee52942c7?q=80&w=1470&auto=format&fit=crop"],
            badges: ["Scripts", "UI", "RPG"],
            category: "Scripts",
            tags: ["Inventory", "UI", "RPG", "Systems"],
            sellerId: "seller101",
            sellerName: "GameSystems",
            rating: 4.7,
            releaseDate: "2023-07-15",
            likes: 1540,
            downloads: 920,
            features: ["Drag & Drop", "Item stacking", "Categories", "Item tooltips"]
          },
          {
            id: 5,
            title: "Procedural Terrain Generator",
            description: "Advanced landscape generation tool with erosion and biome support",
            price: 89.99,
            images: ["https://images.unsplash.com/photo-1500964757637-c85e8a162699?q=80&w=1478&auto=format&fit=crop"],
            badges: ["Tools", "Procedural", "Landscapes"],
            category: "Tools",
            tags: ["Terrain", "Procedural", "Generation", "Editor"],
            sellerId: "seller222",
            sellerName: "TerraMaster",
            rating: 4.9,
            releaseDate: "2023-03-10",
            likes: 2890,
            downloads: 1720,
            features: ["Realistic erosion", "Multiple biomes", "Custom brushes", "Export options"]
          },
          {
            id: 6,
            title: "2D Platformer Character Pack",
            description: "Collection of animated 2D characters with full sprite sheets",
            price: 24.99,
            images: ["https://images.unsplash.com/photo-1472457897821-70d3819a0e24?q=80&w=1469&auto=format&fit=crop"],
            badges: ["2D", "Characters", "Animation"],
            category: "2D Assets",
            tags: ["Sprites", "Characters", "Platformer", "Animation"],
            sellerId: "seller333",
            sellerName: "PixelArtist",
            rating: 4.8,
            releaseDate: "2023-08-20",
            likes: 1680,
            downloads: 1250,
            features: ["Multiple characters", "Idle, run, jump animations", "Modular parts", "PSD source files"]
          },
          {
            id: 7,
            title: "Game Sound Effects Bundle",
            description: "500+ high-quality SFX for various game genres and actions",
            price: 34.99,
            images: ["https://images.unsplash.com/photo-1511379938547-c1f69419868d?q=80&w=1470&auto=format&fit=crop"],
            badges: ["Audio", "SFX", "Bundle"],
            category: "Audio",
            tags: ["Sound Effects", "Audio", "Game Sounds"],
            sellerId: "seller444",
            sellerName: "SoundMaster",
            rating: 4.7,
            releaseDate: "2023-06-05",
            likes: 2240,
            downloads: 1890,
            features: ["WAV & MP3 formats", "Organized by category", "Loop-ready", "Commercial license"]
          },
          {
            id: 8,
            title: "Battle Royale Starter Kit",
            description: "Complete template for creating battle royale games with matchmaking",
            price: 129.99,
            images: ["https://images.unsplash.com/photo-1493711662062-fa541adb3fc8?q=80&w=1470&auto=format&fit=crop"],
            badges: ["Templates", "Multiplayer", "Battle Royale"],
            category: "Templates",
            tags: ["Battle Royale", "Multiplayer", "Networking", "Template"],
            sellerId: "seller555",
            sellerName: "NetworkGuru",
            rating: 4.9,
            releaseDate: "2023-02-15",
            likes: 3150,
            downloads: 1680,
            features: ["Player controller", "Lobby system", "Item pickups", "Zone system"]
          },
        ];

        // Convert API products to CardType
        const convertToCard = (product: ApiProduct, isStaffPick = false, isPopular = false): CardType => ({
          id: product.id,
          title: product.title,
          creator: product.sellerName,
          price: product.price,
          isLiked: false, // This should come from user preferences API
          badges: isStaffPick ? ['Staff Pick', ...product.tags.slice(0, 1)] :
                  isPopular ? ['Popular', ...product.tags.slice(0, 1)] :
                  product.tags.slice(0, 2),
          isNew: new Date(product.releaseDate) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
          discount: Math.random() > 0.7 ? Math.floor(Math.random() * 30) + 10 : undefined, // Random discount for demo
          imageUrl: product.images[0] || "https://images.unsplash.com/photo-1628929441870-959e1be5cb8c?q=80&w=1740&auto=format&fit=crop"
        });

        // Staff picks - curated selection, could be based on editor choice or quality score
        const staffPicks = placeholderProducts
          .slice(0, 8) // Take first 8 for demo purposes
          .map(p => convertToCard(p, true, false));

        // Popular - sort by downloads/likes
        const popular = [...placeholderProducts]
          .sort((a, b) => b.downloads - a.downloads)
          .slice(0, 8)
          .map(p => convertToCard(p, false, true));

        // Recent - sort by release date
        const recent = [...placeholderProducts]
          .sort((a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime())
          .slice(0, 8)
          .map(p => convertToCard(p, false, false));

        setStaffPickCards(staffPicks);
        setPopularCards(popular);
        setRecentCards(recent);
      } catch (error) {
        console.error('Error fetching products:', error);
        // Even if there's an error, we'll still show the UI with placeholder data
        // instead of showing an error message
        setError(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Clear the Recently Viewed items from local storage
  const clearHistory = (e: React.MouseEvent) => {
    e.preventDefault();
    // In a real app, this would clear from state management or localStorage/sessionStorage
    localStorage.removeItem('recentlyViewed');
    alert('History cleared successfully');
    // Reload the page to show the empty state
    window.location.reload();
  };

  // Handle the newsletter subscription
  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // In a real app, this would make an API call to subscribe the email
      // const response = await apiClient.post('/newsletter/subscribe', { email });
      console.log('Subscribing email:', email);
      setSubscribeSuccess(true);
      setEmail('');
      setTimeout(() => setSubscribeSuccess(false), 3000);
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      alert('Failed to subscribe. Please try again later.');
    }
  };

  // Load more staff picks
  const loadMoreStaffPicks = () => {
    setDisplayedStaffPicks(prev => Math.min(prev + 4, staffPickCards.length));
  };

  // Display loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-[#1B1821]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#7F56D9] mx-auto"></div>
          <p className="mt-6 text-[#A3A3A3] text-lg">Loading the marketplace...</p>
        </div>
      </div>
    );
  }

  // Display error state
  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="bg-red-50 p-4 rounded-lg max-w-md">
          <p className="text-red-500 text-center">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 mx-auto block bg-[#7F56D9] hover:bg-[#6941C6] text-white px-4 py-2 rounded"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Hero Section */}
      <Hero />

      {/* Featured Slider */}
      <FeaturedSlider />

      {/* Display login prompt if not authenticated */}
      {!isAuthenticated && (
        <div className="bg-[#2A2633] p-4 mb-8 rounded-lg max-w-4xl mx-auto text-center">
          <p className="text-[#A3A3A3] mb-2">Sign in to save products to your wishlist and make purchases</p>
          <Button
            variant="primary"
            size="md"
            onClick={() => navigate('/login')}
          >
            Sign In
          </Button>
        </div>
      )}

      {/* Staff Picks */}
      <motion.section
        initial="hidden"
        animate={isLoading ? "visible" : "hidden"}
        variants={sectionVariants}
        className="py-12 relative"
        style={{
          transform: 'translateZ(0)',
          position: 'relative',
          contain: 'paint layout',
          minHeight: '650px',
          background: 'linear-gradient(to bottom, #141019, #1A1625)',
        }}
      >
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03]"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center">
              <motion.h2
                className="text-2xl font-bold"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                Staff Picks
              </motion.h2>
              <motion.div
                className="ml-3 bg-[#2A2633] text-xs text-white py-1 px-3 rounded-full"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {staffPickCards.length} items
              </motion.div>
            </div>
            <motion.a
              href="/search?category=staff-picks"
              className="font-semibold hover:underline flex items-center group text-[#F670C7]"
              whileHover={{ x: 5 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              See More
              <svg className="ml-1 w-4 h-4 transform group-hover:translate-x-1 transition-transform" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </motion.a>
          </div>
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
            variants={containerVariants}
            style={{ transform: 'translateZ(0)' }}
          >
            {staffPickCards.slice(0, displayedStaffPicks).map((card) => (
              <CardWrapper key={card.id} variants={cardVariants}>
                <Card
                  title={card.title}
                  creator={card.creator}
                  price={card.price}
                  isLiked={card.isLiked}
                  imageUrl={card.imageUrl}
                  badges={card.badges}
                  isNew={card.isNew}
                  discount={card.discount}
                />
              </CardWrapper>
            ))}
          </motion.div>
          {displayedStaffPicks < staffPickCards.length && (
            <motion.div
              className="mt-8 flex justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <Button
                variant="outline"
                size="md"
                onClick={loadMoreStaffPicks}
                rightIcon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4V20M12 20L18 14M12 20L6 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                }
              >
                Load More
              </Button>
            </motion.div>
          )}
        </div>
      </motion.section>

      {/* Popular Section */}
      <motion.section
        initial="hidden"
        animate={isLoading ? "visible" : "hidden"}
        variants={sectionVariants}
        className="py-12 relative overflow-hidden"
        style={{
          transform: 'translateZ(0)',
          position: 'relative',
          contain: 'paint layout',
          minHeight: '650px',
          background: 'radial-gradient(circle at top right, #2A1E44, #1F1A27 50%)',
        }}
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/10 blur-[100px] rounded-full"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-500/10 blur-[100px] rounded-full"></div>
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03]"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center">
              <motion.h2
                className="text-2xl font-bold"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                Popular
              </motion.h2>
              <motion.div
                className="ml-3 bg-[#2A2633] text-xs text-white py-1 px-3 rounded-full"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {popularCards.length} items
              </motion.div>
            </div>
            <motion.a
              href="/search?category=popular"
              className="font-semibold hover:underline flex items-center group text-[#F670C7]"
              whileHover={{ x: 5 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              See All
              <svg className="ml-1 w-4 h-4 transform group-hover:translate-x-1 transition-transform" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </motion.a>
          </div>
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
            variants={containerVariants}
            style={{ transform: 'translateZ(0)' }}
          >
            {popularCards.map((card) => (
              <CardWrapper key={card.id} variants={cardVariants}>
                <Card
                  title={card.title}
                  creator={card.creator}
                  price={card.price}
                  isLiked={card.isLiked}
                  imageUrl={card.imageUrl}
                  badges={card.badges}
                  isNew={card.isNew}
                  discount={card.discount}
                />
              </CardWrapper>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Stats Section with enhanced styling */}
      <motion.section
        initial="hidden"
        animate={isLoading ? "visible" : "hidden"}
        variants={sectionVariants}
        className="py-16 relative overflow-hidden"
        style={{
          transform: 'translateZ(0)',
          position: 'relative',
          contain: 'paint layout',
          background: 'linear-gradient(to right, #1A1625, #2A1E44)',
        }}
      >
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03]"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/10 blur-[150px] rounded-full"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-500/10 blur-[150px] rounded-full"></div>
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-full h-1/3 bg-gradient-to-t from-[#F670C7]/5 to-transparent"></div>
        <Stats />
      </motion.section>

      {/* Recently Viewed Section */}
      <motion.section
        initial="hidden"
        animate={isLoading ? "visible" : "hidden"}
        variants={sectionVariants}
        className="py-12 relative"
        style={{
          transform: 'translateZ(0)',
          position: 'relative',
          contain: 'paint layout',
          minHeight: '650px',
          background: 'linear-gradient(to bottom, #1A1625, #141019)',
        }}
      >
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='30' height='30' viewBox='0 0 30 30' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M15 0C16.5533 0 17.8182 1.26491 17.8182 2.81818V12.1818H27.1818C28.7351 12.1818 30 13.4467 30 15C30 16.5533 28.7351 17.8182 27.1818 17.8182H17.8182V27.1818C17.8182 28.7351 16.5533 30 15 30C13.4467 30 12.1818 28.7351 12.1818 27.1818V17.8182H2.81818C1.26491 17.8182 0 16.5533 0 15C0 13.4467 1.26491 12.1818 2.81818 12.1818H12.1818V2.81818C12.1818 1.26491 13.4467 0 15 0Z' fill='%23ffffff' opacity='0.03'/%3E%3C/svg%3E")`,
          backgroundSize: '30px 30px',
          backgroundPosition: '0 0',
        }}></div>
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03]"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center">
              <motion.h2
                className="text-2xl font-bold"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                Recently Viewed
              </motion.h2>
              <motion.div
                className="ml-3 bg-[#2A2633] text-xs text-white py-1 px-3 rounded-full"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {recentCards.length} items
              </motion.div>
            </div>
            <motion.a
              href="#"
              onClick={clearHistory}
              className="font-semibold hover:underline flex items-center group text-[#F670C7]"
              whileHover={{ x: 5 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              Clear History
              <svg className="ml-1 w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </motion.a>
          </div>
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
            variants={containerVariants}
            style={{ transform: 'translateZ(0)' }}
          >
            {recentCards.map((card) => (
              <CardWrapper key={card.id} variants={cardVariants}>
                <Card
                  title={card.title}
                  creator={card.creator}
                  price={card.price}
                  isLiked={card.isLiked}
                  imageUrl={card.imageUrl}
                  badges={card.badges}
                  isNew={card.isNew}
                  discount={card.discount}
                />
              </CardWrapper>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Newsletter Section */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="py-16 relative overflow-hidden"
        style={{
          background: 'linear-gradient(to right, #0F0C14, #1A1625)'
        }}
      >
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03]"></div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="bg-gradient-to-r from-[#7F56D9] via-[#9D4EDD] to-[#F670C7] rounded-2xl p-8 text-center relative overflow-hidden"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>
            <div className="relative z-10">
              <motion.span
                className="inline-block px-3 py-1 rounded-full bg-white/20 text-white font-medium text-sm mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                ✉️ Newsletter
              </motion.span>
              <motion.h2
                className="text-3xl font-bold mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                Stay Updated with DevMarket
              </motion.h2>
              <motion.p
                className="text-white/80 mb-8 max-w-2xl mx-auto"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                Subscribe to our newsletter to get the latest updates, tutorials, and exclusive offers delivered straight to your inbox.
              </motion.p>
              <AnimatePresence>
                <motion.form
                  onSubmit={handleSubscribe}
                  className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <motion.input
                    type="email"
                    placeholder="Enter your email"
                    className="input bg-white/20 border-white/20 text-white placeholder:text-white/50 flex-1"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    whileFocus={{ scale: 1.02 }}
                  />
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      variant="secondary"
                      size="md"
                      type="submit"
                      className="!bg-white !text-[#7F56D9] hover:!bg-white/90"
                      rightIcon={
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M22 2L11 13M22 2L15 22L11 13M11 13L2 9L22 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      }
                    >
                      {subscribeSuccess ? 'Subscribed!' : 'Subscribe'}
                    </Button>
                  </motion.div>
                </motion.form>
              </AnimatePresence>
              <motion.p
                className="text-white/60 text-sm mt-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                We respect your privacy. Unsubscribe at any time.
              </motion.p>
            </div>
          </motion.div>
        </div>
      </motion.section>
    </>
  );
};

export default HomePage;
